/**
 * useBitrateAutopilot — watches RTCPeerConnection stats and gracefully
 * steps the outbound video bitrate down when the link gets congested,
 * then back up once it recovers. Keeps broadcasts smooth on flaky LTE
 * without the host having to think about it.
 *
 * Strategy (per peer, every 3 s):
 *   • Compute current packet-loss % and RTT against the last sample.
 *   • If loss > 5% OR rtt > 400 ms → step DOWN one tier (cool-down 10 s).
 *   • If loss < 1% AND rtt < 200 ms for 3 consecutive ticks → step UP.
 *
 * Tiers (bps): 4_000_000 → 2_500_000 → 1_500_000 → 900_000 → 500_000.
 *
 * Returns nothing — the hook just wires up the loop. The caller passes
 * a `getPcs()` accessor so we can iterate without leaking subscriptions.
 */
import { useEffect, useRef } from "react";

const TIERS = [4_000_000, 2_500_000, 1_500_000, 900_000, 500_000];
const TICK_MS = 3000;
const COOLDOWN_TICKS = 4;     // ~12 s before another drop after a step-down
const RECOVERY_TICKS = 3;     // 3 clean ticks in a row before stepping back up

export default function useBitrateAutopilot({ enabled = true, getPcs }) {
  const stateRef = useRef({});
  useEffect(() => {
    if (!enabled || typeof getPcs !== "function") return undefined;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      const pcs = getPcs();
      if (!pcs) return;
      const entries = Array.isArray(pcs) ? pcs : Object.values(pcs);
      for (const entry of entries) {
        const pc = entry?.pc || entry;
        if (!pc || pc.signalingState === "closed") continue;
        try {
          const stats = await pc.getStats();
          let lost = 0, sent = 0, rtt = 0, rttCount = 0;
          stats.forEach((r) => {
            if (r.type === "outbound-rtp" && r.kind === "video") {
              sent += r.packetsSent || 0;
            }
            if (r.type === "remote-inbound-rtp" && r.kind === "video") {
              lost += r.packetsLost || 0;
              if (typeof r.roundTripTime === "number") {
                rtt += r.roundTripTime * 1000;
                rttCount += 1;
              }
            }
          });
          const avgRtt = rttCount ? rtt / rttCount : 0;
          const peerKey = pc.__autopilotKey || (pc.__autopilotKey = Math.random().toString(36).slice(2));
          const st = stateRef.current[peerKey] || (stateRef.current[peerKey] = {
            tier: 0, lastSent: 0, lastLost: 0, cooldown: 0, cleanStreak: 0,
          });
          const deltaSent = sent - st.lastSent;
          const deltaLost = lost - st.lastLost;
          const lossPct = deltaSent > 0 ? (deltaLost / Math.max(1, deltaSent + deltaLost)) * 100 : 0;
          st.lastSent = sent;
          st.lastLost = lost;
          if (st.cooldown > 0) st.cooldown -= 1;

          let newTier = st.tier;
          // Step DOWN if the network looks bad and we're not on cooldown.
          if (st.cooldown === 0 && (lossPct > 5 || avgRtt > 400) && newTier < TIERS.length - 1) {
            newTier += 1;
            st.cooldown = COOLDOWN_TICKS;
            st.cleanStreak = 0;
          } else if (lossPct < 1 && (avgRtt === 0 || avgRtt < 200)) {
            st.cleanStreak += 1;
            if (st.cleanStreak >= RECOVERY_TICKS && newTier > 0) {
              newTier -= 1;
              st.cleanStreak = 0;
            }
          } else {
            st.cleanStreak = 0;
          }
          if (newTier !== st.tier) {
            st.tier = newTier;
            const cap = TIERS[newTier];
            pc.getSenders().forEach((sender) => {
              if (sender.track?.kind !== "video") return;
              const params = sender.getParameters();
              if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
              params.encodings.forEach((enc) => { enc.maxBitrate = cap; });
              sender.setParameters(params).catch(() => {});
            });
          }
        } catch { /* peer closed mid-tick — fine */ }
      }
    };

    const id = setInterval(tick, TICK_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, getPcs]);
}
